import { supabase } from "../supabase/SupabaseClient";
import { createRow, updateRow } from "./supabaseRealtime";

/* Lo que hace falta en el portón: ¿este camión puede salir?
 *
 * El RTO lo lee de nuestra base, no del CENT en vivo. El portón no puede
 * esperar cuatro segundos a un sitio de terceros con el chofer parado
 * adelante, y menos depender de que esté arriba. Se sincroniza de noche.
 *
 * El veredicto lo arma la base, no esta pantalla: si la regla de los 30 días
 * viviera acá y en el reporte y en el aviso, tarde o temprano dirían cosas
 * distintas y nadie sabría cuál creer.
 */

export async function estadoRTO(dominio) {
  const { data, error } = await supabase.rpc("rto_estado", {
    p_dominio: dominio,
  });
  if (error) throw error;
  return data?.[0] || null;
}

/* ── LA FECHA QUE SE LEE DEL TÍTULO ───────────────────────────────────────
 *
 * Un 0 km no rinde RTO por un año, así que el CENT no tiene nada suyo y el hub
 * lo dibujaba como `sin_rto`. La fecha está impresa en el título y no había
 * dónde escribirla — son 142 unidades activas de la compra 2025/2026.
 *
 * VA POR RPC Y NO POR UN `.insert()`. `rto_manual` no tiene política de
 * escritura: un insert desde el navegador no escribiría nada y PostgREST
 * contestaría 200 con cero filas, o sea la pantalla diciendo «listo» sin haber
 * guardado — el 200-que-no-es-un-éxito de `/api/sync-docs`. Además la función
 * es la que SELLA quién firmó: el nombre se resuelve del lado del servidor,
 * porque una firma que manda el cliente no es una firma.
 *
 * El permiso se verifica adentro (`cargar.documentos`). Esconder el campo no
 * es un permiso.
 */
export async function declararRTO(dominio, vence, motivo) {
  const { data, error } = await supabase.rpc("declarar_rto", {
    p_dominio: dominio,
    p_vence: vence,
    p_motivo: motivo || null,
  });
  if (error) throw new Error(error.message);
  return data?.[0] || null;
}

/* Acá borrar SÍ es borrar, y es la excepción justificable a la regla de la
 * casa. Una fecha del título mal tipeada no es un hecho de la operación que
 * haya que conservar: es un error de carga, y dejarlo «anulado» lo mantendría
 * compitiendo en `rto_estado` con el bueno. El botón de al lado la reescribe. */
export async function borrarRTODeclarada(dominio) {
  const { error } = await supabase.rpc("borrar_rto_declarada", {
    p_dominio: dominio,
  });
  if (error) throw new Error(error.message);
}

/* El historial: todos los años, no sólo el vigente. Sirve para ver quién
 * renueva puntual y quién siempre llega sobre la hora, que no es lo mismo que
 * "hoy está al día". */
export async function historialRTO(dominio) {
  const limpio = (dominio || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!limpio) return [];
  const { data, error } = await supabase
    .from("rto")
    .select("vence, revisado, resultado, tipo, certificado, centro")
    .eq("dominio", limpio)
    .order("vence", { ascending: false });
  if (error) throw error;
  return data || [];
}

/* Todo lo que hay que mirar: vencidos, por vencer y sin RTO de la flota que
 * trabaja. Es la lista que Marcos necesita el lunes a la mañana, no cuando el
 * chofer ya está en el portón. */
/* TODOS los tractores con su RTO. Hermana de `furgonesRTO()`.
 *
 * NO es `pendientesRTO()`, que mezcla las dos flotas y sólo trae lo pendiente:
 * esa pregunta —«qué hay para renovar en toda la flota»— es la que contesta el
 * bot por WhatsApp, y sigue siendo suya. */
export async function tractoresRTO() {
  const { data, error } = await supabase.rpc("rto_tractores");
  if (error) throw error;
  return data || [];
}

/* Cuándo se vio moverse por última vez a cada unidad de un padrón.
 *
 * Devuelve un `Map` de interno → fecha, que es como lo consume `shared/rto`
 * para decidir el tag «Sin uso?».
 *
 * ── SÍ, ES UNA SEGUNDA CONSULTA, Y ACÁ SÍ SE PUEDE ────────────────────────
 *
 * `furgonesRTO` dice justo lo contrario sobre la ubicación —viene ADENTRO de
 * la fila, porque emparejar dos listas en el navegador falla el día que
 * devuelven conjuntos distintos y la tabla muestra una fila sin ubicación sin
 * que eso signifique nada—. La objeción es la misma y la diferencia es a dónde
 * cae el error.
 *
 * Allá, un interno que no empareja BORRA una ubicación que existe: la celda
 * miente hacia el lado de «no sé». Acá, un interno que no empareja deja la fila
 * sin `visto_en`, y sin `visto_en` la unidad se queda en «Vencido» — que es
 * exactamente lo que hubiera pasado sin esta consulta. El emparejamiento fallido
 * no puede esconder una vencida; en el peor caso no la mueve.
 *
 * Y no se mete adentro de `rto_tractores`/`rto_furgones` porque cambiarles el
 * tipo de retorno obliga a dropearlas y recrearlas, y son las dos funciones de
 * las que depende el veredicto del portón. No vale la pena tocarlas para esto.
 *
 * Falla en silencio a propósito: sin actividad no hay tag y la pantalla dice lo
 * de siempre. Un cartel rojo por esto haría que una lista que funciona parezca
 * rota.
 */
export async function actividadDe(padron) {
  const { data, error } = await supabase
    .from("ultima_actividad")
    .select("interno, ultima")
    .eq("padron", padron);
  if (error) {
    console.warn("[rto] no pude leer la actividad:", error.message);
    return null;
  }
  return new Map((data || []).map((f) => [String(f.interno), f.ultima]));
}

/* El archivo de unidades que ya no son de la flota (ticket 155 punto 4).
 *
 * Entero y de una vez: son 99 filas de una tabla que no cambia —es un archivo,
 * no un padrón—, así que traerlas todas una vez cuesta menos que consultar por
 * cada búsqueda. El emparejamiento se hace en el navegador, en `shared/buscar`.
 *
 * Devuelve `[]` si falla, y a propósito: no poder mostrar un camión vendido en
 * 2019 no puede romper la búsqueda de los que están trabajando hoy.
 */
export async function unidadesHistoricas() {
  const { data, error } = await supabase
    .from("unidades_historicas")
    .select("dominio, interno_planilla, empresa, marca, modelo, anio, estado_planilla");
  if (error) {
    console.warn("[buscador] sin el archivo de unidades:", error.message);
    return [];
  }
  return data || [];
}

export async function pendientesRTO() {
  const { data, error } = await supabase.rpc("rto_pendientes");
  if (error) throw error;
  return data || [];
}

/* Busca un camión por patente o por interno. Las dos cosas se usan: la patente
 * está en el papel, el interno es como lo llaman todos. */
export async function buscarCamion(texto) {
  const q = (texto || "").trim();
  if (!q) return [];
  const patente = q.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const { data, error } = await supabase
    .from("tractores")
    .select("interno, dominio, marca, modelo, empresa, activo")
    .or(`dominio.ilike.%${patente}%,interno.eq.${q.replace(/\D/g, "") || 0}`)
    .limit(20);
  if (error) throw error;
  return data || [];
}

/* Todos los furgones con su RTO — los que están al día también.
 *
 * Es otra consulta que la de tractores y no un parámetro, porque de los
 * furgones no sabemos nada más: no tienen GPS ni kilómetros, así que la
 * pantalla es un listado y no una ficha con posición y proyección.
 *
 * Vienen ~680 filas de una sola vez: el filtro y el orden se hacen en el
 * navegador, sin volver a pedir en cada tecla.
 *
 * La última ubicación viene ADENTRO de estas filas (`ubic_*`) y no en una
 * segunda consulta. Emparejar dos listas en el navegador funciona hasta el día
 * que devuelvan conjuntos distintos —una unidad nueva, un interno que cambió—
 * y ahí la tabla muestra una fila sin ubicación sin que eso signifique nada.
 */
export async function furgonesRTO() {
  const { data, error } = await supabase.rpc("rto_furgones");
  if (error) throw error;
  return data || [];
}

/* Dónde se vio por última vez una unidad, de a una.
 *
 * NO ES GPS y no puede serlo: la telemetría vive en FlotaBot y los furgones ni
 * siquiera la tienen. Sale de las dos fuentes que sí escribe gente —el portón y
 * el movimiento de unidades— y la respuesta trae `fuente` justamente para que
 * no se lean igual: portería es de hoy, el movimiento viene atrasado.
 *
 * Devuelve `null` cuando no hay ningún registro, y eso NO es un error: hay
 * unidades de las que no hay nada anotado. Tratarlo como falla haría que la
 * ficha muestre un cartel rojo por un dato que falta.
 */
export async function ubicacionDe(tipo, interno) {
  if (!interno) return null;
  const { data, error } = await supabase.rpc("ubicacion_de", {
    p_tipo: tipo,
    p_interno: String(interno),
  });
  if (error) throw error;
  return data?.[0] || null;
}

/* ─────────────────────────────────────────────────────────────────────────
 * LA AGENDA DE TURNOS
 *
 * `rto` es lo que dice el CENT y es lo único que decide si un camión puede
 * salir. Esto es lo que planificó una persona: cuándo lo llevan y a dónde.
 * Son dos cosas y no se mezclan — un turno es una intención y el portón sólo
 * puede creerle a un hecho.
 * ───────────────────────────────────────────────────────────────────────── */

/* El próximo turno de UNA unidad, para mostrarlo en su ficha.
 *
 * SÓLO LOS ABIERTOS (`pedido` / `confirmado`). Un turno ya hecho es historia, y
 * puesto al lado del vencimiento se lee como «ya tiene el RTO» — que es
 * exactamente lo que esta tabla existe para no decir. El que quiera el
 * historial abre la agenda.
 *
 * Va por PATENTE porque así va `rto_turnos`, que no distingue tractor de
 * furgón. Es lo que hace que la misma agenda sirva para los acoplados.
 *
 * Devuelve `null` cuando no hay ninguno, y eso NO es un error: la enorme
 * mayoría de las unidades no tiene turno sacado.
 */
export async function proximoTurnoRTO(dominio) {
  const limpio = (dominio || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!limpio) return null;
  const { data, error } = await supabase
    .from("rto_turnos")
    .select("fecha, hora, planta, estado, obs")
    .eq("dominio", limpio)
    // Desde el #111 la agenda también guarda turnos de choferes (registro,
    // peligrosas). Acá se pregunta por el RTO de UNA unidad, así que se filtra
    // por tipo aunque el dominio ya no pueda chocar: un turno de chofer no
    // tiene patente.
    .eq("tipo", "rto")
    .in("estado", ["pedido", "confirmado"])
    // El más próximo primero: si hay dos abiertos, el que viene es el que
    // importa. Que haya dos es un dato en sí y se ve en la agenda.
    .order("fecha", { ascending: true })
    .limit(1);
  if (error) throw error;
  return data?.[0] || null;
}

export async function agendaRTO() {
  const { data, error } = await supabase.rpc("rto_agenda");
  if (error) throw error;
  return data || [];
}

/* Las plantas ya usadas, ordenadas por uso. Sugieren y no restringen: el campo
 * sigue siendo de texto libre, así que una planta nueva se escribe igual. Es
 * lo que permite que la lista se arme sola sin que nadie abra un ABM. */
export async function plantasRTO() {
  const { data, error } = await supabase.rpc("rto_plantas");
  if (error) throw error;
  return data || [];
}

/* Las escrituras pasan por `createRow` / `updateRow` y no por un insert propio:
 * ése es el cuello donde se sella quién cargó cada fila. Un insert suelto en la
 * pantalla nacería sin autor y nadie se enteraría hasta que el dato hiciera
 * falta — que es exactamente el problema que `quien.js` vino a resolver.
 *
 * Y viven acá y no en el componente para que la pantalla no tenga que saber
 * cómo se llama la tabla. */
export const crearTurnoRTO = (datos) => createRow("rto_turnos", null, datos);

export const cambiarEstadoTurnoRTO = (id, estado) =>
  updateRow("rto_turnos", id, { estado });

/* ─────────────────────────────────────────────────────────────────────────
 * DÓNDE ESTÁ UN CHOFER
 *
 * Portería anota, en el mismo evento, quién cruzó y con qué unidad. De ahí
 * sale la cadena: chofer → interno → dónde está ese interno.
 *
 * NO se usa el tractor «habitual», y ésa es la decisión que sostiene todo:
 * hay 3,1 tractores distintos por chofer de promedio y 82 de 258 tienen su
 * último cruce con uno distinto del que más usan. Contestar por el habitual
 * erraría de camión una de cada tres veces.
 *
 * Devuelve `null` cuando esa persona nunca cruzó — que es lo normal para los
 * 2.100 del padrón que no manejan, y no es un error.
 * ───────────────────────────────────────────────────────────────────────── */
export async function dondeEstaElChofer(dni) {
  if (!dni) return null;
  const { data, error } = await supabase.rpc("ubicaciones_choferes", {
    p_dni: String(dni),
  });
  if (error) throw error;
  return data?.[0] || null;
}
